/**
 * Algolia Search Integration (Products + Articles Multi-Index)
 * Powers predictive search & /search results page with 65%/35% split layout.
 * Includes Theme Grid Overrides for template-search.
 */
(function () {
  'use strict';

  // ==========================================
  // CONFIGURATION & INITIALIZATION
  // ==========================================

  if (!window.algoliaConfig) {
    console.warn('Algolia config not found');
    return;
  }

  const {
    appId,
    searchKey,
    indexName,           // Articles index (petrolicious_articles)
    productsIndexName = 'petrolicious_products', // Products index
    hitsPerPage = 6,
    searchPageEnabled = true,
    searchPageResults = 24
  } = window.algoliaConfig;

  if (!appId || !searchKey || !indexName) {
    console.warn('Algolia credentials incomplete');
    return;
  }

  const ARTICLE_ATTRIBUTES = [
    'objectID', 'title', 'url', 'handle',
    'image', 'featured_image', 'published_at',
    'created_at', 'author', 'blog_handle', 'blog'
  ];

  const PRODUCT_ATTRIBUTES = [
    'objectID', 'title', 'url', 'handle',
    'image', 'featured_image', 'product_image',
    'price', 'price_min', 'vendor', 'product_type', 'collections', 'variants'
  ];

  let articlesIndex = null;
  let productsIndex = null;

  function initAlgolia() {
    if (typeof algoliasearch === 'undefined') {
      setTimeout(initAlgolia, 100);
      return;
    }
    
    const client = algoliasearch(appId, searchKey);
    articlesIndex = client.initIndex(indexName);
    if (productsIndexName) {
      productsIndex = client.initIndex(productsIndexName);
    }

    injectLayoutStyles();
    overridePredictiveSearch();
    initSearchResultsPage();
  }

  // ==========================================
  // LAYOUT STYLES & THEME GRID OVERRIDES
  // ==========================================

  function injectLayoutStyles() {
    if (document.getElementById('algolia-layout-styles')) return;

    const style = document.createElement('style');
    style.id = 'algolia-layout-styles';
    style.textContent = `
      /* OVERRIDE SHOPIFY THEME SEARCH GRID (Prevents squishing on /search page) */
      body.template-search #grid-product,
      #grid-product.algolia-search-grid {
        display: block !important;
        width: 100% !important;
        max-width: 100% !important;
        grid-template-columns: none !important;
        float: none !important;
      }

      .search-split--two-col {
        display: flex;
        gap: 2rem;
        width: 100%;
        align-items: flex-start;
      }
      .search-split--two-col .search-split__articles {
        flex: 0 0 65%;
        width: 65%;
      }
      .search-split__articles .search-articles__grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 1.25rem;
      }
      .search-split--two-col .search-split__products {
        width: 35%;
        border-left: 1px solid #e5e5e5;
        padding-left: 1.5rem;
      }
      .search-split__products .search-products__stack {
        display: flex;
        flex-direction: column;
        gap: 0.85rem;
      }
      .search-section__title {
        font-size: 1.4rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: #111;
        padding-bottom: 0.5rem;
        margin-bottom: 1rem;
      }

      /* COMPACT HORIZONTAL PRODUCT CARD (Image Left | Info Right) */
      .card--product-horizontal {
        display: flex;
        align-items: flex-start;
        gap: 0.85rem;
        padding: 0.5rem 0;
        text-decoration: none;
        border-bottom: 1px solid #f0f0f0;
        transition: opacity 0.2s ease;
      }
      .card--product-horizontal:last-child {
        border-bottom: none;
      }
      .card--product-horizontal:hover {
        opacity: 0.8;
      }
      .card--product-horizontal .card__image-wrap {
        width: 75px;
        height: 75px;
        flex-shrink: 0;
        border-radius: 4px;
        overflow: hidden;
        background-color: #f7f7f7;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .card--product-horizontal .card__image-wrap img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
      .card--product-horizontal .card__info {
        display: flex;
        flex-direction: column;
        justify-content: center;
        flex: 1;
        min-width: 0;
      }
      .card--product-horizontal .card__meta {
        font-size: 1rem;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: #777;
        margin-bottom: 0.2rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .card--product-horizontal .card__title {
        font-size: 1.4rem;
        font-weight: 600;
        color: #111;
        line-height: 1.25;
        margin: 0 0 0.25rem 0;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
      .card--product-horizontal .card__price {
        font-size: 1.2rem;
        font-weight: 700;
        color: #222;
        margin: 0;
      }

      .search-split--single-col .search-split__articles {
        width: 100%;
      }
      .search-split--single-col .search-articles__grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 1.25rem;
      }
      .badge-type {
        font-size: 0.7rem;
        text-transform: uppercase;
        background: #eee;
        padding: 2px 6px;
        border-radius: 3px;
        margin-right: 6px;
      }

      @media (max-width: 989px) {
        .search-split--two-col {
          flex-direction: column;
        }
        .search-split--two-col .search-split__articles,
        .search-split--two-col .search-split__products {
          flex: 0 0 100%;
          width: 100%;
          border-left: none;
          padding-left: 0;
        }
        .search-split__articles .search-articles__grid {
          grid-template-columns: repeat(2, 1fr);
        }
      }
    `;
    document.head.appendChild(style);
  }

  // ==========================================
  // SHARED UTILITIES
  // ==========================================

  function highlightText(text, searchTerms) {
    if (!text || !searchTerms) return text || '';
    const terms = searchTerms.toLowerCase().split(/\s+/).filter(t => t.length > 0);
    let result = text;
    terms.forEach(term => {
      const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      result = result.replace(regex, '<mark>$1</mark>');
    });
    return result;
  }

  function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  function getArticleUrl(hit) {
    if (hit.url) return hit.url;
    const blogHandle = hit.blog_handle || (hit.blog && hit.blog.handle);
    if (blogHandle && hit.handle) return `/blogs/${blogHandle}/${hit.handle}`;
    return '#';
  }

  function getProductUrl(hit) {
    if (hit.url) return hit.url;
    if (hit.handle) return `/products/${hit.handle}`;
    return '#';
  }

  function formatPrice(hit) {
    const rawPrice = hit.price ?? hit.price_min;
    if (rawPrice === undefined || rawPrice === null) return '';
    const num = typeof rawPrice === 'number' ? rawPrice : parseFloat(rawPrice);
    if (isNaN(num)) return '';
    return `$${num.toFixed(2)}`;
  }

  // ==========================================
  // CARD RENDERERS
  // ==========================================

  function renderArticleCard(hit) {
    const imageUrl = hit.image || hit.featured_image || '';
    const title = hit.title || '';
    const url = getArticleUrl(hit);

    const authorName = hit.author
      ? (typeof hit.author === 'object' ? (hit.author.name || '') : hit.author)
      : 'Petrolicious';
    let publishDate = '';
    if (hit.published_at || hit.created_at) {
      const dateObj = new Date(hit.published_at || hit.created_at);
      publishDate = dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }

    return `
      <article class="card card--article card--style-padding">
        <a class="card__image" href="${url}">
          <figure class="aspect-ratio--landscape aspect-ratio__mobile--landscape">
            ${imageUrl ? `<img src="${imageUrl}" alt="${escapeHtml(title)}" class="img-absolute" loading="lazy" width="400" height="300">` : ''}
          </figure>
        </a>
        <a href="${url}" class="card__content">
          <div>
            ${authorName || publishDate ? `<p class="card__blog-title text-xs">by ${escapeHtml(authorName)}${publishDate ? ` – ${publishDate}` : ''}</p>` : ''}
            <p class="card__title">${escapeHtml(title)}</p>
          </div>
          <div class="card__bottom">
            <p class="button button--primary">${window.searchStrings?.read_more || 'Read Article'}</p>
          </div>
        </a>
      </article>
    `;
  }

  function renderProductCard(hit) {
    const imageUrl = hit.image || hit.featured_image || hit.product_image || '';
    const title = hit.title || '';
    const url = getProductUrl(hit);
    const priceFormatted = formatPrice(hit);
    
    const collectionName = (Array.isArray(hit.collections) && hit.collections.length > 0)
      ? hit.collections[0]
      : (hit.product_type || hit.vendor || 'Shop');

    return `
      <a href="${url}" class="card--product-horizontal">
        <div class="card__image-wrap">
          ${imageUrl ? `<img src="${imageUrl}" alt="${escapeHtml(title)}" loading="lazy" width="75" height="75">` : ''}
        </div>
        <div class="card__info">
          ${collectionName ? `<span class="card__meta">${escapeHtml(collectionName)}</span>` : ''}
          <h4 class="card__title">${escapeHtml(title)}</h4>
          ${priceFormatted ? `<span class="card__price">${priceFormatted}</span>` : ''}
        </div>
      </a>
    `;
  }

  // ==========================================
  // PREDICTIVE SEARCH — SIDEBAR & GRID
  // ==========================================

  function renderSidebar(articleHits, productHits, searchTerms) {
    let html = '';

    if (productHits.length > 0) {
      const productItems = productHits.slice(0, 4).map(hit => {
        const title = hit._highlightResult?.title?.value || highlightText(hit.title, searchTerms);
        return `<li class="searches-search__item"><a href="${getProductUrl(hit)}"><span class="badge-type">Product</span> ${title}</a></li>`;
      }).join('');

      html += `
        <div class="search-sidebar__list">
          <p class="search-sidebar__header text-md">${window.searchStrings?.products || 'Shop'}</p>
          <ul class="product-search">${productItems}</ul>
        </div>
      `;
    }

    if (articleHits.length > 0) {
      const articleItems = articleHits.slice(0, 4).map(hit => {
        const title = hit._highlightResult?.title?.value || highlightText(hit.title, searchTerms);
        return `<li class="searches-search__item"><a href="${getArticleUrl(hit)}">${title}</a></li>`;
      }).join('');

      html += `
        <div class="search-sidebar__list">
          <p class="search-sidebar__header text-md">${window.searchStrings?.articles || 'Articles'}</p>
          <ul class="article-search">${articleItems}</ul>
        </div>
      `;
    }

    if (articleHits.length === 0 && productHits.length === 0) {
      html = `
        <div class="search-sidebar__list">
          <p class="search-sidebar__header text-md">${window.searchStrings?.title_no_results_article || 'No results found'}</p>
        </div>
      `;
    }

    return `<div class="search-sidebar__results">${html}</div>`;
  }

  function renderGrid(articleHits, productHits, searchTerms) {
    if (articleHits.length === 0 && productHits.length === 0) {
      return `
        <div class="no-results-notice">
          <p>${window.searchStrings?.no_results_predictive || 'No results found for your search.'}</p>
        </div>
      `;
    }

    const hasArticles = articleHits.length > 0;
    const hasProducts = productHits.length > 0;

    let articlesHtml = '';
    if (hasArticles) {
      const sortedArticles = [...articleHits].sort((a, b) => {
        return new Date(b.published_at || 0) - new Date(a.published_at || 0);
      });
      const articleCards = sortedArticles.map(hit => renderArticleCard(hit)).join('');
      articlesHtml = `
        <div class="search-split__articles">
          <h3 class="search-section__title">Articles (${articleHits.length})</h3>
          <div class="search-articles__grid">
            ${articleCards}
          </div>
        </div>
      `;
    }

    let productsHtml = '';
    if (hasProducts) {
      const productCards = productHits.map(hit => renderProductCard(hit)).join('');
      productsHtml = `
        <div class="search-split__products">
          <h3 class="search-section__title">Shop (${productHits.length})</h3>
          <div class="search-products__stack">
            ${productCards}
          </div>
        </div>
      `;
    }

    const splitClass = (hasArticles && hasProducts) ? 'search-split--two-col' : 'search-split--single-col';

    let viewAllHtml = '';
    const totalHits = articleHits.length + productHits.length;
    if (totalHits >= hitsPerPage) {
      const encodedTerms = encodeURIComponent(searchTerms);
      viewAllHtml = `
        <div class="search-grid__footer width-full" style="width: 100%; text-align: center; margin-top: 1.5rem;">
          <a href="/search?q=${encodedTerms}" class="button button--primary">
            ${window.searchStrings?.view_all || 'View all results'}
          </a>
        </div>
      `;
    }

    return `
      <div class="search-products__row">
        <div class="search-split ${splitClass}">
          ${articlesHtml}
          ${productsHtml}
        </div>
        ${viewAllHtml}
      </div>
    `;
  }

  // ==========================================
  // PREDICTIVE SEARCH OVERRIDE
  // ==========================================

  function overridePredictiveSearch() {
    if (!customElements.get('predictive-search')) {
      setTimeout(overridePredictiveSearch, 100);
      return;
    }

    document.querySelectorAll('predictive-search').forEach(element => {
      element.search = async function (event) {
        const query = event.target.value;

        if (!query || !articlesIndex) return;

        const resultsContainer = this.querySelector('.predictive-search__results-container');
        if (!resultsContainer) return;

        resultsContainer.classList.add('loading');

        try {
          const searchPromises = [
            articlesIndex.search(query, {
              hitsPerPage: hitsPerPage,
              attributesToRetrieve: ARTICLE_ATTRIBUTES,
              attributesToHighlight: ['title']
            })
          ];

          if (productsIndex) {
            searchPromises.push(
              productsIndex.search(query, {
                hitsPerPage: Math.floor(RESULTS_PER_PAGE * 0.35),
                attributesToRetrieve: PRODUCT_ATTRIBUTES,
                attributesToHighlight: ['title']
              })
            );
          }

          const [articlesResult, productsResult] = await Promise.all(searchPromises);
          const articleHits = articlesResult?.hits || [];
          const productHits = productsResult?.hits || [];

          // Update sidebar
          const sidebar = resultsContainer.querySelector('.search-sidebar');
          if (sidebar) {
            let sidebarInner = sidebar.querySelector('#search-sidebar');
            if (!sidebarInner) {
              sidebarInner = document.createElement('div');
              sidebarInner.id = 'search-sidebar';
              sidebarInner.classList.add('search-sidebar__initial');
              sidebar.prepend(sidebarInner);
            }
            const existingResults = sidebar.querySelector('.search-sidebar__results');
            if (existingResults) existingResults.remove();
            sidebar.insertAdjacentHTML('beforeend', renderSidebar(articleHits, productHits, query));

            if (this.populateRecentSearches) {
              this.populateRecentSearches(resultsContainer);
            }
          }

          // Update grid
          const productsSection = resultsContainer.querySelector('.search-products');
          if (productsSection) {
            productsSection.innerHTML = renderGrid(articleHits, productHits, query);
          }

        } catch (error) {
          console.error('Algolia predictive search error:', error);
        } finally {
          resultsContainer.classList.remove('loading');
        }
      };
    });

    console.log('Algolia 65%/35% multi-index search initialized');
  }

  // ==========================================
  // SEARCH RESULTS PAGE (/search)
  // ==========================================

  const RESULTS_PER_PAGE = searchPageResults;
  let currentPage = 0;
  let currentQuery = '';

  function isSearchResultsPage() {
    return document.body.classList.contains('template-search') ||
      window.location.pathname === '/search';
  }

  async function performSearchPageSearch(query, page = 0) {
    if (!articlesIndex || !query) return;

    currentQuery = query;
    currentPage = page;

    const gridContainer = document.getElementById('grid-product');
    if (!gridContainer) return;

    // Add class to apply theme layout reset CSS
    gridContainer.classList.add('algolia-search-grid');
    gridContainer.style.opacity = '0.3';
    gridContainer.style.pointerEvents = 'none';

    try {
      const searchPromises = [
        articlesIndex.search(query, {
          hitsPerPage: Math.floor(RESULTS_PER_PAGE * 0.65),
          page: page,
          attributesToRetrieve: ARTICLE_ATTRIBUTES,
          attributesToHighlight: ['title']
        })
      ];

      if (productsIndex) {
        searchPromises.push(
          productsIndex.search(query, {
            hitsPerPage: Math.floor(RESULTS_PER_PAGE * 0.35),
            page: page,
            attributesToRetrieve: PRODUCT_ATTRIBUTES,
            attributesToHighlight: ['title']
          })
        );
      }

      const [articlesResult, productsResult] = await Promise.all(searchPromises);
      const articleHits = articlesResult?.hits || [];
      const productHits = productsResult?.hits || [];
      const totalHits = (articlesResult?.nbHits || 0) + (productsResult?.nbHits || 0);

      const heading = document.querySelector('.search-results h1');
      if (heading && totalHits > 0) {
        heading.textContent = `Search results for "${escapeHtml(query)}"`;
      }

      if (articleHits.length === 0 && productHits.length === 0) {
        gridContainer.innerHTML = `
          <p class="grid-archive__no-results">
            ${window.searchStrings?.nothing_found || 'No results found'}
          </p>
        `;
      } else {
        gridContainer.innerHTML = renderGrid(articleHits, productHits, query);
      }

      const newUrl = new URL(window.location);
      newUrl.searchParams.set('q', query);
      window.history.pushState({}, '', newUrl);

    } catch (error) {
      console.error('Algolia search page error:', error);
    } finally {
      gridContainer.style.opacity = '1';
      gridContainer.style.pointerEvents = 'auto';
    }
  }

  function initSearchResultsPage() {
    if (!isSearchResultsPage() || !searchPageEnabled) return;

    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get('q');
    const page = parseInt(urlParams.get('page') || '1', 10) - 1;

    if (query) {
      performSearchPageSearch(query, Math.max(0, page));
    }
  }

  // ==========================================
  // BOOT
  // ==========================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAlgolia);
  } else {
    initAlgolia();
  }
})();